import { match } from "@stepapp/core";
import { Block, BottomSheet, Button, Typography, palette, spacing } from "@stepapp/ui";
import { useRouter } from "expo-router";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Platform, StyleSheet } from "react-native";
import { useCopyToClipboard } from "../../hooks";
import { AppStorage } from "../../services/AppStorage";

type ModifyReservationProps = {
  isVisible: boolean;
  promoCode?: string;
  subscriptionCancellationFee?: string;
  toggle: () => void;
  handleCancelSubscription: () => void;
  newAddressDiscount?: boolean;
};

const CancelSubscriptionPromoCodeSheet = ({
  isVisible,
  promoCode = "",
  subscriptionCancellationFee = "",
  toggle,
  handleCancelSubscription,
  newAddressDiscount = false,
}: ModifyReservationProps) => {
  const { t } = useTranslation();
  const { replace } = useRouter();
  const countryCode = AppStorage().get("countryCode");

  const copyToClipboard = useCopyToClipboard(promoCode);

  const discount = match(countryCode)
    .with("PL", () => "75 PLN")
    .with("NO", () => "500 NOK")
    .otherwise(() => "");

  const handleClose = useCallback(() => {
    toggle();
  }, [toggle]);

  const handleUsePromoCode = useCallback(() => {
    copyToClipboard();
    toggle();
    setTimeout(
      () =>
        replace({
          pathname: "/payments",
        }),
      500,
    );
  }, [copyToClipboard, toggle, replace]);

  const handleConfirm = useCallback(() => {
    toggle();

    setTimeout(() => handleCancelSubscription(), 500);
  }, [toggle, handleCancelSubscription]);

  const descriptionSecondPart = newAddressDiscount
    ? t("screens.cancelReservation.newAddressDiscount")
    : t("screens.cancelReservation.maybeConsiderResubscribe");

  const descriptionFirstPart =
    subscriptionCancellationFee && `${t("screens.cancelReservation.specialOffer")} `;

  return (
    <BottomSheet
      contentStyle={styles.container}
      enableScroll={false}
      toggle={handleClose}
      visible={isVisible}
    >
      <Typography variant="newH2">
        {subscriptionCancellationFee
          ? t("screens.cancelReservation.insteadOfPaying", {
              fee: subscriptionCancellationFee,
              discount,
            })
          : t("screens.cancelReservation.specialOffer")}
      </Typography>
      <Typography variant="paragraph" type="secondary" style={styles.description}>
        {`${descriptionFirstPart}${descriptionSecondPart}`}
      </Typography>

      <Block alignCenter justifyCenter marginVertical={1.5} style={styles.couponContainer}>
        <Typography variant="bodyBold">
          {t("screens.cancelReservation.nextServiceDiscount", { discount })}
        </Typography>
        <Typography center variant="lightLabel" style={styles.discountDescription}>
          {t("screens.cancelReservation.addDiscount", { discount })}
        </Typography>
        <Typography style={styles.coupon} variant="bodyBold">
          {promoCode}
        </Typography>
      </Block>

      <Button
        label={t("screens.cancelReservation.staySubscribed")}
        onPress={handleUsePromoCode}
        type="primary"
        style={styles.button}
      />
      <Button
        label={t("screens.cancelReservation.cancelSubscription", {
          fee: subscriptionCancellationFee && `(${subscriptionCancellationFee})`,
        })}
        onPress={handleConfirm}
        type="secondary"
        style={styles.button}
      />
    </BottomSheet>
  );
};

export default CancelSubscriptionPromoCodeSheet;

const styles = StyleSheet.create({
  button: {
    ...Platform.select({
      default: {},
      web: { marginTop: spacing.unit * 1.5 },
    }),
  },
  coupon: {
    backgroundColor: palette.secondary.wrongPink,
    color: palette.common.white,
    paddingHorizontal: spacing.unit * 2,
    paddingVertical: spacing.unit * 1.5,
  },
  couponContainer: {
    backgroundColor: palette.common.pinkSalmon,
    minHeight: spacing.unit * 7,
    minWidth: spacing.unit * 24,
    padding: spacing.unit * 3,
  },
  container: {
    padding: spacing.unit * 2.5,
  },
  discountDescription: {
    paddingBottom: spacing.unit * 2,
    paddingTop: spacing.unit * 1.5,
  },
  description: {
    marginTop: spacing.unit,
    marginBottom: spacing.unit,
  },
});
